/**
 * capAuthBridge
 *
 * MFE auth single-call bridge. In the MFE setup the host (cap-intouch-ui) is the
 * ONLY app that hits the auth endpoint (/arya/api/v1/auth/user). Remotes must not
 * make their own auth call; instead they consume the host's response through the
 * globals published here.
 *
 * What this module publishes on `window`:
 *   - window.getCapAuthData(): Promise<fullResponse>
 *       A deferred promise, created at module load so a remote that asks BEFORE
 *       the host's auth call completes simply waits. Once the host resolves it,
 *       every later caller gets the cached response instantly.
 *   - window.capAuth: the auth "superset" — the frozen object produced by
 *       Auth.initialize (permissions / roles / isCapUser / accessibleFeatures,
 *       used by hasAccess / hasFeatureAccess) OVERLOADED with the full auth
 *       response fields (success, user, currentOrgDetails, currentOrgId,
 *       systemTimeZone, ...). Remotes also use its presence as the signal to
 *       skip their own Auth.initialize.
 *   - window.capAuthData: the raw full auth response (sync convenience accessor).
 *
 * The host calls publishCapAuth(response) from its user-info saga once the auth
 * response is in hand; failCapAuth(error) is called if that request fails so the
 * remotes' promise rejects and their normal failure path runs.
 */

import { Auth } from '@capillarytech/cap-ui-utils';

let resolveAuth;
let rejectAuth;
let settled = false;

const authPromise = new Promise((resolve, reject) => {
  resolveAuth = resolve;
  rejectAuth = reject;
});

if (typeof window !== 'undefined') {
  // Return the same promise on every call. Resolves once the host's auth call is
  // done; instant for later callers.
  window.getCapAuthData = () => authPromise;
}

/**
 * Publish the host's auth response to the MFE bridge.
 * @param {object} response full /auth/user response
 */
export function publishCapAuth(response) {
  if (!response) return;
  const { user = {}, currentOrgDetails = {} } = response;

  // Host still initializes auth the standard way — sets the frozen basic shape
  // (permissions / roles / isCapUser / accessibleFeatures) that hasAccess &
  // hasFeatureAccess read.
  try {
    Auth.initialize({
      permissions: user.accessiblePermissions,
      isCapUser: user.isCapUser,
      roles: user.accessRoles ? Object.keys(user.accessRoles) : undefined,
      accessibleFeatures: currentOrgDetails.accessibleFeatures,
    });
  } catch (e) {
    // Auth.initialize logs its own warnings; don't block the bridge on it.
  }

  // Overload window.capAuth into the superset: keep the basic keys just set by
  // Auth.initialize and add the full response fields. Re-freeze a new object
  // (the previous one is frozen; reassigning the window property is allowed).
  if (typeof window !== 'undefined') {
    try {
      window.capAuth = Object.freeze({ ...(window.capAuth || {}), ...response });
    } catch (e) {
      window.capAuth = { ...(window.capAuth || {}), ...response };
    }
    window.capAuthData = response;
  }

  if (!settled) {
    settled = true;
    resolveAuth(response);
  }
}

/**
 * Reject the bridge promise so remotes fall through to their failure handling.
 * @param {*} error
 */
export function failCapAuth(error) {
  if (!settled) {
    settled = true;
    rejectAuth(error);
  }
}
